/* eslint-disable import/prefer-default-export */
import React from 'react';
import { Box } from '@peoplecues/commons';
import { Avatar } from '@peoplecues/commons';
import { dim } from 'styles/theme';
import { UserRole } from '../../../../utils/constants';

export const transformString = string => {
  if (string && string.length) {
    let strArray = string.split(' ');
    strArray = strArray.map(str =>
      str && str[0] ? str[0].toUpperCase() + str?.slice(1) : ''
    );
    return strArray.join(' ');
  }
  return string;
};

export const UserNameColumn = React.memo(function UserNameColumn({ user }) {
  return (
    <Box d="flex" alignItems="center" maxWidth={dim._scale(240)}>
      <Avatar size="M" name={user.name} src={user.image} my={dim._16px} />
      <Box
        ml={dim._8px}
        color="#010101"
        className="tag14"
        maxWidth={dim._scale(200)}>
        {transformString(user.name)}
      </Box>
    </Box>
  );
});

export const UserEmailColumn = React.memo(function UserEmailColumn({ email }) {
  return (
    <Box color="#2B2B2B" className="tag14" maxWidth={dim._scale(200)}>
      {email}
    </Box>
  );
});

export const UserDepartmentColumn = React.memo(function UserDepartmentColumn({
  department
}) {
  return (
    <Box color="#202124" className="tag14">
      {department}
    </Box>
  );
});

export const UserActionColumn = React.memo(function UserActionColumn({
  user,
  role,
  removeUser
}) {
  return (
    <Box
      onClick={() => removeUser(user)}
      color="#DF2E2E"
      cursor="pointer"
      opacity={
        (role.access !== 'SUPER_ADMIN' &&
          user.user_product_details_list.findIndex(
            p =>
              p.product_reference_id === 'apps' && p.user_role === 'SUPER_ADMIN'
          ) > -1) ||
        (role.access !== 'SUPER_HR' &&
          user.user_product_details_list.findIndex(
            p =>
              p.product_reference_id === 'people' && p.user_role === 'SUPER_HR'
          ) > -1)
          ? '50%'
          : 1
      }
      fontWeight="500">
      Remove
    </Box>
  );
});

export const RolesSettings = {
  'Super Administrator': {
    heading:
      'Usually Founders, CXOs and HR Heads are made the Super Administrator',
    permissions: ['They have all the access']
  },
  'Goals Administrator': {
    heading:
      'Usually Chief of Staff or Strategy Leader is made a Goals Administrator',
    permissions: [
      'Make someone Goals administrator',
      'Create company objectives',
      'Update check-in, delete any objectives',
      'Change OKR module configuration'
    ]
  },
  'Performance Administrator': {
    heading:
      'Usually Senior HR managing review cycle is made Performance Administrator',
    permissions: [
      'Make someone Performance administrator',
      'Create reviews',
      'Manage reviews'
    ]
  },
  Administrator: {
    heading: 'Usually an IT member tracking active users is made Administrator',
    permissions: [
      'User data management (invite, remove, update)',
      'Make someone administrator'
    ]
  },
  'Engagement Administrator': {
    heading:
      'Usually Senior HR managing employee engagement is made Engagement Administrator',
    permissions: [
      'Make someone Engagement administrator or survey creator',
      'Create & manage an org level survey',
      'Create & manage custom/ follow-up surveys',
      'View participation & results of an org level or custom/ follow-up survey',
      'Publish report of an org level or custom/ follow-up survey'
    ]
  },
  'Survey Creator': {
    heading: 'Usually HRBPs managing surveys/AMAs are made Survey Creators',
    permissions: [
      'Make someone survey creator',
      'Create & manage an org level survey',
      'Create & manage custom/ follow-up surveys',
      'View participation of an org level or custom/ follow-up survey'
    ]
  },
  HRBP: {
    heading: (
      <div>
        The <b>HR Business Partner</b> is a strategic liaison between HR and the
        business or managers.
      </div>
    ),
    permissions: [
      'Drive Survey & Performance Review participation of their respective teams',
      'Access to Survey Reports of their respective teams',
      'Access to Performance Review forms of their respective teams'
    ]
  },
  '1:1 Administrator': {
    heading:
      'Usually Senior HR managing all one-on-one is made 1:1 Administrator',
    permissions: ['Make someone 1:1 administrator', 'Manage org one-on-ones']
  }
};

export const isHrbpRoleVisible = userRoles =>
  ['SUPER_HR', 'ADMIN'].includes(userRoles.people) ||
  [UserRole.SUPER_ADMIN, UserRole.ADMIN].includes(userRoles.apps);

export const isHrbpRoleWritable = userRoles =>
  [UserRole.SUPER_HR].includes(userRoles.people) ||
  [UserRole.SUPER_ADMIN, UserRole.ADMIN].includes(userRoles.apps);

export const canUpdateHrbpRole = userRoles =>
  userRoles.people === 'SUPER_HR' ||
  [UserRole.SUPER_ADMIN, UserRole.ADMIN].includes(userRoles.apps);

export const getHrbpColumns = userRoles => {
  const columns = ['NAME', 'EMAIL', 'ASSIGNED TO'];
  if (canUpdateHrbpRole(userRoles)) columns.push('ACTIONS');
  return columns;
};

export const isHrbpEnabled = (
  accessibleModules,
  userRoles,
  featureFlagConfig,
  readOnly = true
) =>
  (readOnly ? isHrbpRoleVisible(userRoles) : isHrbpRoleWritable(userRoles)) &&
  featureFlagConfig?.hrbp;

export const createHrbpUserList = (hrbpResponse, allUsersMap) => {
  const newHrbpList = [];
  if (hrbpResponse?.hrbp_users_map) {
    Object.entries(hrbpResponse?.hrbp_users_map).forEach(
      ([hrbpUserId, managersList]) => {
        if (allUsersMap?.[hrbpUserId]) {
          const hrbp = { ...allUsersMap[hrbpUserId] };
          hrbp.id = hrbpUserId;
          hrbp.assigned_managers = [];
          Object.values(managersList)?.forEach(managerId => {
            if (allUsersMap?.[managerId]) {
              hrbp.assigned_managers.push({
                ...allUsersMap[managerId]
              });
            }
          });
          newHrbpList.push(hrbp);
        }
      }
    );
  }
  return newHrbpList;
};
